import {
  Box,
  Typography,
  Grid2 as Grid,
  Divider,
  ThemeProvider
} from '@mui/material'
import { createAppTheme } from 'styles/theme'
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { EXAM_TYPE_MAP } from '../../constants'
import { ecgTypes } from 'pages/Diagnostics/constants/diagnostic_types'
import PrintECGExam from './ECG/PrintECGExam'
import PrintXRayExam from './XRay/PrintXRayExam'
import { IExamResponse } from 'types/exam/types'
import { ECGDiagnosticCode } from 'pages/Diagnostics/types'
import { ReportSignature } from './ReportSignature'
import saLogo from 'assets/logo_sa_minimal.svg'

interface PrintableReportProps {
  diagnosticDetail: IExamResponse
  /**
   * Aceito para paridade com o fluxo de raio-X, mas hoje não é impresso: o
   * bloco do ECG não tem seção de anotações.
   */
  ecgAnnotations?: Record<string, string>
  xrayAnnotations?: Record<string, string>
  medicalReport?: string
  /**
   * Logotipo do estabelecimento. Recebido por prop de propósito: este
   * componente também é renderizado num root React destacado na geração do PDF
   * (`Details/index.tsx`), fora do `<Provider>`, então ler o Redux aqui dentro
   * quebraria aquele fluxo.
   */
  logoUrl?: string | null
  /**
   * Disparado quando o relatório está pronto para ser capturado — o que, no
   * raio-X, depende do download e da compressão da imagem original. A geração
   * do PDF espera este sinal em vez de um atraso fixo.
   */
  onReady?: () => void
}

export const PrintableReport = ({
  diagnosticDetail,
  xrayAnnotations = {},
  medicalReport = '',
  logoUrl,
  onReady
}: PrintableReportProps) => {
  const isXRay = diagnosticDetail.type !== 'ecg'
  // Força tema light para impressão
  const lightTheme = createAppTheme('light')

  // O bucket precisa responder com CORS para o html2canvas conseguir desenhar a
  // imagem (o PDF é gerado com useCORS/allowTaint:false). Se falhar, o
  // cabeçalho degrada para o layout sem logotipo em vez de imagem quebrada.
  const [logoFailed, setLogoFailed] = useState(false)

  // Instante em que o laudo foi emitido, congelado no primeiro render: a data
  // da análise, o carimbo de assinatura e o rodapé precisam contar a mesma
  // hora, e o carimbo mostra até os segundos.
  const [signedAt] = useState(() => new Date())

  // O bloco de ECG desenha a partir de dados já em memória, sem nada
  // assíncrono, então nasce pronto. No raio-X quem sinaliza é o
  // `PrintXRayExam`, que precisa baixar e comprimir a imagem original antes. O
  // logotipo não entra na conta em nenhum dos dois: o gerador já espera pelas
  // `<img>` do documento antes de capturar.
  useEffect(() => {
    if (!isXRay) onReady?.()
  }, [isXRay, onReady])

  return (
    <ThemeProvider theme={lightTheme}>
      <Box
        sx={{
          width: '100%',
          maxWidth: '21cm',
          margin: '0 auto',
          padding: '1cm',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          backgroundColor: '#ffffff !important',
          color: '#000000 !important',
          fontSize: '10pt',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
          fontFeatureSettings: '"kern" 1',
          fontKerning: 'normal',
          '& .MuiTypography-root': {
            color: '#000000 !important'
          },
          '@media print': {
            backgroundColor: '#ffffff !important',
            color: '#000000 !important',
            '& .MuiTypography-root': {
              color: '#000000 !important'
            }
          }
        }}
        className='printable-report'
      >
        <Box
          component='table'
          sx={{ width: '100%', borderCollapse: 'collapse' }}
        >
          <thead>
            <tr>
              <td style={{ padding: 0 }}>
                {/* Cabeçalho da Revisão Médica */}
                <Box
                  data-pdf-header
                  sx={{
                    mb: 2,
                    borderBottom: '2px solid #000',
                    pb: 1,
                    pageBreakInside: 'avoid',
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    gap: 1
                  }}
                >
                  <Typography
                    variant='subtitle1'
                    sx={{
                      fontWeight: 'bold',
                      width: '200px',
                      height: '50px',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center'
                    }}
                  >
                    Revisão Médica ({EXAM_TYPE_MAP[diagnosticDetail.type]})
                  </Typography>

                  <Box>
                    {logoUrl && !logoFailed && (
                      <img
                        src={logoUrl}
                        alt='Logotipo do estabelecimento'
                        crossOrigin='anonymous'
                        onError={() => setLogoFailed(true)}
                        style={{
                          maxHeight: '50px'
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: 0 }}>
                {/* Dados do Paciente e Exame */}
                <Box data-pdf-section sx={{ mb: 2, pageBreakInside: 'avoid' }}>
                  <Grid container>
                    <Grid size={12}>
                      <Typography variant='body2'>
                        <strong>Idenficador da Análise:</strong>{' '}
                        {diagnosticDetail.id_exam}
                      </Typography>
                    </Grid>
                    <Grid size={12}>
                      <Typography variant='body2'>
                        <strong>Identificador do Paciente:</strong>{' '}
                        {diagnosticDetail.patient_id}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant='body2'>
                        <strong>Data do Exame:</strong>{' '}
                        {dayjs(diagnosticDetail.created_at).format(
                          'DD/MM/YYYY'
                        )}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant='body2'>
                        <strong>Data da Análise:</strong>{' '}
                        {dayjs(signedAt).format('DD/MM/YYYY')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Renderização por tipo de exame */}
                {!isXRay ? (
                  <>
                    {/* Página 1: Header + Leads */}
                    <Box
                      data-pdf-section
                      sx={{ mb: 2, pageBreakInside: 'avoid' }}
                    >
                      <Typography
                        variant='subtitle1'
                        sx={{ mb: 1, fontWeight: 'bold' }}
                      >
                        Visualização do Eletrocardiograma
                      </Typography>
                      <Box
                        sx={{
                          '& .MuiTypography-root': {
                            color: '#000000 !important'
                          }
                        }}
                      >
                        <PrintECGExam exameData={diagnosticDetail} />
                      </Box>
                    </Box>

                    {/* Página 2: Análises */}
                    {diagnosticDetail.exam_results.length > 0 && (
                      <Box
                        data-pdf-section
                        sx={{ mb: 2, pageBreakInside: 'avoid' }}
                      >
                        <Typography
                          variant='subtitle1'
                          sx={{ mb: 1, fontWeight: 'bold' }}
                        >
                          Análise
                        </Typography>
                        {diagnosticDetail.exam_results.map(
                          (diagnosisCode, index) => {
                            const diagnosisData =
                              ecgTypes[
                                diagnosisCode.result_type as ECGDiagnosticCode
                              ]
                            if (!diagnosisData) return null

                            return (
                              <Box
                                key={`${diagnosisCode.result_type}-${index}`}
                                sx={{
                                  mb: 1,
                                  p: 1,
                                  backgroundColor: '#f5f5f5',
                                  borderLeft: '3px solid #333',
                                  pageBreakInside: 'avoid'
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.9rem'
                                  }}
                                >
                                  {diagnosisData.name}
                                </Typography>
                                <Typography
                                  variant='caption'
                                  sx={{ mt: 0.5, display: 'block' }}
                                >
                                  <strong>Código:</strong>{' '}
                                  {diagnosisCode.result_type}
                                </Typography>
                                <Typography
                                  variant='caption'
                                  sx={{ mt: 0.5, display: 'block' }}
                                >
                                  <strong>Descrição:</strong>{' '}
                                  {diagnosisData.description}
                                </Typography>
                                <Typography
                                  variant='caption'
                                  sx={{ mt: 0.5, display: 'block' }}
                                >
                                  <strong>Derivações afetadas:</strong>{' '}
                                  {diagnosisData.leads.join(', ')}
                                </Typography>
                              </Box>
                            )
                          }
                        )}
                      </Box>
                    )}
                  </>
                ) : (
                  <PrintXRayExam
                    examData={diagnosticDetail}
                    xrayAnnotations={xrayAnnotations}
                    onReady={onReady}
                  />
                )}

                <Divider sx={{ my: 1.5 }} />

                {/* Revisão Médico Completo */}
                <Box
                  data-pdf-section
                  sx={{
                    mb: 2,
                    pageBreakInside: 'avoid',
                    pageBreakBefore: 'auto'
                  }}
                >
                  <Typography
                    variant='subtitle1'
                    sx={{ mb: 1, fontWeight: 'bold' }}
                  >
                    Revisão Médica
                  </Typography>
                  <Typography
                    sx={{
                      p: 1.5,
                      backgroundColor: '#fafafa',
                      minHeight: '60px',
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.6,
                      pageBreakInside: 'avoid'
                    }}
                  >
                    {medicalReport || 'Nenhuma revisão médica registrada.'}
                  </Typography>
                </Box>

                {/* Assinatura. Precisa estar dentro de um `data-pdf-section`:
            o gerador de PDF monta o documento percorrendo só essas seções, e
            tudo que fica de fora é descartado silenciosamente. */}
                <Box
                  data-pdf-section
                  sx={{
                    pageBreakInside: 'avoid'
                  }}
                >
                  <Box sx={{ pt: 2 }}>
                    <ReportSignature signedAt={signedAt} logoUrl={logoUrl} />
                  </Box>
                </Box>
              </td>
            </tr>
          </tbody>
          {/* Na impressão, o `<tfoot>` se repete no pé de toda página. No PDF,
          `useGeneratePDF` recorta o `data-pdf-footer` e o desenha no rodapé de
          cada página, fora do fluxo das seções. */}
          <tfoot>
            <tr>
              <td style={{ padding: 0 }}>
                <Box
                  data-pdf-footer
                  sx={{
                    pt: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.75
                  }}
                >
                  <Typography sx={{ fontSize: '6.5pt', lineHeight: 1 }}>
                    powered by:
                  </Typography>
                  <img
                    src={saLogo}
                    alt='swissanalytica'
                    style={{ height: '12px', width: 'auto' }}
                  />
                </Box>
              </td>
            </tr>
          </tfoot>
        </Box>
      </Box>
    </ThemeProvider>
  )
}
